import { Box, Link, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import Paragraph from '../../shared/UI/Paragraph'
import { absenceFormLabels } from '../../utils/constants'
import { removeTrailingZeroes } from '../../utils/conversion'
import InfoBanner from '../../shared/UI/InfoBanner'
import {
  isPayrollNoticeAbsence,
  isCompassionateOtherPartnerChildUnwellHeader,
  getCompassionateHeaderText,
  getHeaderText,
} from './utils'

type InfoBannerWithHandbookProps = {
  headerText: string
  bodyText: string
  employeeHandbookUrl?: string
}

function InfoBannerWithHandbook({
  headerText,
  bodyText,
  employeeHandbookUrl: handbookUrl,
}: InfoBannerWithHandbookProps) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
      <Box>
        <Paragraph weight="bold" size="13px" sx={{ marginBottom: '4px' }}>
          {headerText}
        </Paragraph>
        <Typography variant="body2" component="div" sx={{ color: '#000000' }}>
          {bodyText}
        </Typography>
        <Typography variant="body2" component="div" sx={{ color: '#000000' }}>
          <Link
            href={handbookUrl || '#'}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: 'inherit',
              textDecoration: 'underline',
            }}
          >
            See employee handbook
          </Link>
        </Typography>
      </Box>
    </Box>
  )
}

type OSPNotificationProps = {
  exceededHours: number
  onApprovalHoursChange?: (value: number | null) => void
  NonSicknessManAuth?: boolean
  absenceTypeId?: number
  employeeHandbookUrl?: string
}

export default function OSPNotificationWithApproval({
  exceededHours,
  NonSicknessManAuth = false,
  absenceTypeId,
  employeeHandbookUrl,
}: OSPNotificationProps) {
  const defaultNotificationText = (
    <>
      The duration of this absence exceeds the employee&apos;s OSP allowance by
      {' '}
      <span style={{ fontWeight: 'bold' }}>{removeTrailingZeroes(exceededHours, 2)}</span>
      {' '}
      hours. In exceptional circumstances a discretionary payment can be requested, you must discuss this with your payroll approver who will subsequently be required to approve the payment.
    </>
  )

  const nonSicknessManAuthText = (
    <>
      This Non-Sickness absence requires manager authorization. You can request approval for up to
      {' '}
      <span style={{ fontWeight: 'bold' }}>{removeTrailingZeroes(exceededHours, 2)}</span>
      hours to be paid.
    </>
  )

  const MaternityPaternityJuryServiceText = (
    <InfoBannerWithHandbook
      headerText={absenceFormLabels.MaternityPaternityJuryServiceHeader}
      bodyText="Please ensure you notify payroll of this absence."
      employeeHandbookUrl={employeeHandbookUrl}
    />
  )

  const CompassionateOtherPartnerChildUnwellText = (
    <InfoBannerWithHandbook
      headerText={getCompassionateHeaderText(absenceTypeId)}
      bodyText="Any discretionary pay requests will be escalated to the payroll approver"
      employeeHandbookUrl={employeeHandbookUrl}
    />
  )

  const getDisplayText = () => {
    if (isPayrollNoticeAbsence(absenceTypeId)) {
      return MaternityPaternityJuryServiceText
    }
    if (isCompassionateOtherPartnerChildUnwellHeader(absenceTypeId)) {
      return CompassionateOtherPartnerChildUnwellText
    }
    if (NonSicknessManAuth) {
      return nonSicknessManAuthText
    }
    return defaultNotificationText
  }

  const usesInfoLayout =
    isPayrollNoticeAbsence(absenceTypeId) ||
    isCompassionateOtherPartnerChildUnwellHeader(absenceTypeId)

  return (
    <>
      <InfoBanner
        dataTestId="manauth-approval-banner"
        title={usesInfoLayout ? undefined : getHeaderText(absenceTypeId)}
        margins={NonSicknessManAuth ? { left: '0', right: '0' } : undefined}
        icon={usesInfoLayout ? null : undefined}
      >
        {getDisplayText()}
      </InfoBanner>
    </>
  )
}
